/**
 * Invoice Detail Page — /office/billing/invoices/<id>/
 *
 * Replaces the ~180 lines of inline JS and 12 inline `onclick=` handlers that
 * previously lived in `templates/office/invoices/detail.html`.
 *
 * Why this module exists (not just CSP cleanup):
 *   The old inline `<script>` blocks lived INSIDE `#invoice-detail`. Each time
 *   a form on this page HTMX-swaps the container (record a payment, send the
 *   invoice, apply credit, …) those scripts re-ran — re-registering their
 *   document-level listeners (`htmx:configRequest`, `keydown` Escape,
 *   `htmx:afterSettle`) without removing the previous ones. After a handful
 *   of actions the page ended up with N duplicate handlers all mutating the
 *   same HTMX request or all trying to close modals. Centralising the
 *   wiring here and guarding attachment with `globalBound` kills that leak.
 *
 * Wiring strategy:
 *   - Global behaviours (modal triggers, HTMX spinners on the known
 *     form/button ids, reverse payment confirm) are all event-delegated
 *     from `document.body` and attached EXACTLY ONCE per page visit.
 *   - The credit-note modal AND the send-invoice modal both follow the
 *     canonical "shell + dynamic content" pattern (see ``form-modals.mdc``
 *     + ``utils/ui/dynamic-modal.js``): the shell is static, the form is
 *     fetched on every open. The CN amount-link / vat-link widgets
 *     re-init in the CN modal's ``onContentInjected`` callback.
 *
 * Template contract:
 *   - Root container: `<div id="invoice-detail" data-auto-init="invoiceDetail"
 *                      data-remaining-amount="..." data-invoice-subtotal="..."
 *                      data-invoice-vat="..." data-max-net-amount="..."
 *                      data-cn-create-url="...">`
 *   - Send-invoice trigger: `data-send-inv-open data-send-inv-url="..."
 *     data-send-inv-title="..."` — same shell #send-inv-modal serves
 *     both DRAFT (finalize+send) and ISSUED/OVERDUE (resend), each
 *     trigger pointing at the correct endpoint.
 *   - CN modal trigger: `data-invoice-modal-open="create-cn-modal"` — the
 *     URL/title used to fetch the form come from the root `data-cn-create-url`
 *     attribute and the i18n title text on the trigger button (`data-cn-modal-title`).
 *   - Reverse payment button: `data-reverse-payment-id="<id>"` +
 *     `data-reverse-title`, `data-reverse-message`, `data-reverse-confirm-text`,
 *     `data-reverse-cancel-text`, `data-reverse-input-label`,
 *     `data-reverse-input-placeholder`
 */

import { lockButton, unlockButton } from '../utils/forms/submit-button.js';
import { createDynamicModal } from '../utils/ui/dynamic-modal.js';
import { sanitizeMoneyField, parseMoney, formatMoney, roundCents } from '../utils/forms/money.js';
import { showToast } from '../utils/ui/toast.js';

// HTMX-driven buttons on this page that should show a spinner + stay disabled
// until the response settles. (Send-invoice / send-email no longer live
// here: they go through the canonical dynamic-modal helper which owns
// its own submit lifecycle.)
var HTMX_SPINNER_BUTTONS = [
    'send-reminder-btn',
    'apply-credit-btn',
];

var HTMX_SPINNER_FORM_IDS = ['sidebar-payment-form'];

var globalBound = false;
var cnModal = null;
var sendInvModal = null;

// ========================================================
// CN modal — canonical dynamic shell, see ``form-modals.mdc``.
// Re-init the credit-note widgets after every content swap
// (initial GET and validation 422 re-render).
// ========================================================

function initCnModal() {
    if (cnModal) return;
    cnModal = createDynamicModal({
        modalId: 'create-cn-modal',
        onContentInjected: function () {
            var root = document.querySelector('[data-auto-init="invoiceDetail"]');
            if (!root) return;
            if (typeof window.initCreditNoteCreate === 'function') {
                window.initCreditNoteCreate({
                    invoiceSubtotal: parseFloat(root.dataset.invoiceSubtotal || '0') || 0,
                    invoiceVat: parseFloat(root.dataset.invoiceVat || '0') || 0,
                    maxNetAmount: parseFloat(root.dataset.maxNetAmount || '0') || 0,
                });
            }
            var modalEl = document.getElementById('create-cn-modal');
            if (modalEl) {
                if (typeof window.reinitFlowbiteWidgets === 'function') {
                    window.reinitFlowbiteWidgets(modalEl);
                }
                // Upgrade the Reason <select.form-select> to the project
                // combobox (idempotent — scoped to the modal subtree).
                if (typeof window.initCustomSelects === 'function') {
                    window.initCustomSelects(modalEl);
                }
            }
        },
        onSuccess: function (data) {
            if (data && data.redirect_url) {
                window.location.assign(data.redirect_url);
            }
        },
    });
}

// ========================================================
// Send-Invoice modal — canonical dynamic shell. The same
// shell (#send-inv-modal) serves DRAFT (finalize + send) and
// ISSUED/OVERDUE (resend); each trigger button carries its
// own ``data-send-inv-url`` pointing at the right endpoint.
// ========================================================

function initSendInvModal() {
    if (sendInvModal) return;
    sendInvModal = createDynamicModal({
        modalId: 'send-inv-modal',
        onSuccess: function (data, helper) {
            // afterClose so the soft-reload / redirect fires once the
            // leave transition is fully done — otherwise the Pattern B
            // partial swap destroys the backdrop mid-fade and the user
            // sees a "white flash".
            helper.close(() => {
                if (data && data.redirect_url) {
                    window.location.href = data.redirect_url;
                } else if (typeof window._softReload === 'function') {
                    window._softReload();
                } else {
                    window.location.reload();
                }
            });
        },
        errorLoadingMessage: 'Could not load the send form. Please try again.',
    });
}

// ========================================================
// DELEGATED CLICK: modal triggers + reverse-payment confirm
// ========================================================

function handleDelegatedClick(evt) {
    var t = evt.target;
    if (!t || !t.closest) return;

    var sendInvBtn = t.closest('[data-send-inv-open]');
    if (sendInvBtn) {
        evt.preventDefault();
        if (!sendInvModal) return;
        var sendUrl = sendInvBtn.dataset.sendInvUrl;
        if (!sendUrl) return;
        var sendTitle = sendInvBtn.dataset.sendInvTitle || 'Send Invoice';
        sendInvModal.open(sendUrl, sendTitle);
        return;
    }

    var openBtn = t.closest('[data-invoice-modal-open]');
    if (openBtn) {
        var openId = openBtn.dataset.invoiceModalOpen;

        if (openId === 'create-cn-modal') {
            var root = document.querySelector('[data-auto-init="invoiceDetail"]');
            var url = root && root.dataset.cnCreateUrl;
            if (!url || !cnModal) return;
            var title = openBtn.dataset.cnModalTitle || openBtn.textContent.trim() || 'Create Credit Note';
            cnModal.open(url, title);
            return;
        }
        // No other static modal triggers remain on this page.
        return;
    }

    // Draft line editing: show / hide the "add manual line" row. The row
    // itself is server-rendered inside #invoice-lines-block; HTMX swaps
    // re-render it hidden, so the toggle state naturally resets after
    // every successful mutation.
    var addLineToggle = t.closest('[data-add-line-toggle]');
    if (addLineToggle) {
        var addRow = document.getElementById('invoice-add-line-row');
        if (!addRow) return;
        addRow.classList.remove('hidden');
        var descInput = addRow.querySelector('input[name="description"]');
        if (descInput) descInput.focus();
        return;
    }
    var addLineCancel = t.closest('[data-add-line-cancel]');
    if (addLineCancel) {
        var openRow = document.getElementById('invoice-add-line-row');
        if (openRow) openRow.classList.add('hidden');
        return;
    }

    var delDraftBtn = t.closest('[data-delete-draft]');
    if (delDraftBtn) {
        if (typeof window.showConfirmModal !== 'function') return;
        var dds = delDraftBtn.dataset;
        window.showConfirmModal({
            title: dds.deleteDraftTitle || 'Delete this draft?',
            message: dds.deleteDraftMessage || '',
            confirmText: dds.deleteDraftConfirm || 'Delete draft',
            cancelText: dds.deleteDraftCancel || 'Cancel',
            type: 'danger',
            onConfirm: function () {
                var form = document.getElementById('delete-draft-form');
                if (form) form.requestSubmit();
            },
        });
        return;
    }

    var revBtn = t.closest('[data-reverse-payment-id]');
    if (revBtn) {
        if (typeof window.showConfirmModal !== 'function') return;
        var pid = revBtn.dataset.reversePaymentId;
        var ds = revBtn.dataset;
        window.showConfirmModal({
            title: ds.reverseTitle || 'Reverse payment?',
            message: ds.reverseMessage || '',
            confirmText: ds.reverseConfirmText || 'Reverse',
            cancelText: ds.reverseCancelText || 'Cancel',
            type: 'danger',
            inputLabel: ds.reverseInputLabel || '',
            inputPlaceholder: ds.reverseInputPlaceholder || '',
            onConfirm: function (reason) {
                var reasonInput = document.getElementById('reverse-reason-' + pid);
                var form = document.getElementById('reverse-payment-form-' + pid);
                if (reasonInput) reasonInput.value = reason || '';
                if (form) form.requestSubmit();
            },
        });
    }
}

// ========================================================
// DELEGATED INPUT: overpayment warning on #sidebar-amount
// ========================================================

// Money sanitize/parse live in utils/forms/money.js (shared with
// job-form, credit-notes and the global data-money-input sanitizer in
// app.js); PaymentForm's DecimalTextInput converts the comma back to
// a dot on POST.
function handleOverpaymentInput(evt) {
    var t = evt.target;
    if (!t || t.id !== 'sidebar-amount') return;

    sanitizeMoneyField(t);

    var root = document.querySelector('[data-auto-init="invoiceDetail"]');
    if (!root) return;
    var remaining = parseFloat(root.dataset.remainingAmount || '0') || 0;

    var warning = document.getElementById('overpayment-warning');
    var amountSpan = document.getElementById('overpayment-amount');
    if (!warning || !amountSpan) return;

    var val = parseMoney(t.value) || 0;
    if (val > remaining && remaining > 0) {
        amountSpan.textContent = (val - remaining).toFixed(2).replace('.', ',') + ' €';
        warning.classList.remove('hidden');
    } else {
        warning.classList.add('hidden');
    }
}

// ========================================================
// DELEGATED INPUT: live qty × unit-price preview on the add-line row
// ========================================================

// The add row commits via an explicit "Add" button, so the TOTAL cell
// would otherwise stay blank until submit — recompute it on every
// keystroke so the operator sees the resulting amount immediately.
function handleAddLineTotalInput(evt) {
    var t = evt.target;
    if (!t || (t.name !== 'quantity' && t.name !== 'unit_price')) return;
    var row = t.closest('#invoice-add-line-row');
    if (!row) return;

    var totalEl = row.querySelector('[data-add-line-total]');
    if (!totalEl) return;

    var qtyInput = row.querySelector('input[name="quantity"]');
    var priceInput = row.querySelector('input[name="unit_price"]');
    var qty = qtyInput ? (parseMoney(qtyInput.value) || 0) : 0;
    var price = priceInput ? (parseMoney(priceInput.value) || 0) : 0;
    totalEl.textContent = formatMoney(roundCents(qty * price)) + '\u00a0€';
}

// ========================================================
// DELEGATED HTMX before/after-request: spinner on known buttons
// ========================================================

function findSpinnerButton(evt) {
    var elt = evt.detail && evt.detail.elt;
    if (!elt) return null;
    if (elt.tagName === 'FORM' && HTMX_SPINNER_FORM_IDS.indexOf(elt.id) !== -1) {
        return elt.querySelector('button[type="submit"]');
    }
    if (elt.tagName === 'BUTTON' && HTMX_SPINNER_BUTTONS.indexOf(elt.id) !== -1) {
        return elt;
    }
    if (elt.form && HTMX_SPINNER_FORM_IDS.indexOf(elt.form.id) !== -1) {
        return elt;
    }
    return null;
}

function handleHtmxBeforeRequest(evt) {
    var btn = findSpinnerButton(evt);
    if (!btn) return;
    lockButton(btn);
}

function handleHtmxAfterRequest(evt) {
    var btn = findSpinnerButton(evt);
    if (!btn) return;
    unlockButton(btn);
}

// ========================================================
// Draft line editing — #invoice-lines-block HTMX plumbing.
//
// The block's hx-* attributes (row save-on-change, add form,
// delete button) are fully declarative; the JS below only adds
// the cross-swap niceties: error toasts on business refusals,
// the project confirm modal for line deletion, and focus
// restoration after a swap (saving a row replaces the whole
// block, which would otherwise drop the field the user had
// just tabbed into).
// ========================================================

var _pendingLineFocus = null;

function isInLinesBlock(elt) {
    return !!(elt && elt.closest && elt.closest('#invoice-lines-block'));
}

function handleLinesBlockBeforeRequest(evt) {
    if (!isInLinesBlock(evt.detail && evt.detail.elt)) return;
    var ae = document.activeElement;
    if (ae && ae.dataset && ae.dataset.lineField) {
        _pendingLineFocus = { id: ae.dataset.lineId, field: ae.dataset.lineField };
    } else {
        _pendingLineFocus = null;
    }
}

function handleLinesBlockSettle(evt) {
    var t = evt.detail && evt.detail.target;
    if (!t || t.id !== 'invoice-lines-block') return;
    // The swap replaced the VAT <select.form-select> of the add row with
    // a fresh native one — rebuild the styled combobox, scoped to the
    // block so the rest of the page's selects are untouched.
    var liveBlock = document.getElementById('invoice-lines-block');
    if (liveBlock && typeof window.initCustomSelects === 'function') {
        window.initCustomSelects(liveBlock);
    }
    if (!_pendingLineFocus) return;
    var sel = '[data-line-id="' + _pendingLineFocus.id + '"]' +
              '[data-line-field="' + _pendingLineFocus.field + '"]';
    var input = document.querySelector(sel);
    _pendingLineFocus = null;
    if (input) {
        input.focus();
        if (typeof input.select === 'function') input.select();
    }
}

// Business refusal (400: not DRAFT anymore, negative total, bad value…)
// or permission loss (403): surface the server's message as an error
// toast. The block is NOT swapped on 4xx, so the user's input stays in
// place for correction; the service already rolled the mutation back.
function handleLinesBlockResponseError(evt) {
    if (!isInLinesBlock(evt.detail && evt.detail.elt)) return;
    var xhr = evt.detail && evt.detail.xhr;
    var msg = '';
    if (xhr && (xhr.status === 400 || xhr.status === 403) && xhr.responseText) {
        msg = xhr.responseText;
    }
    var root = document.querySelector('[data-auto-init="invoiceDetail"]');
    var fallback = (root && root.dataset.lineErrorDefault) || 'Could not save the line. Please try again.';
    showToast(msg || fallback, 'error');
}

// Replace the native hx-confirm dialog with the project confirm modal
// for line deletion. hx-confirm stays on the button as the no-JS-modal
// fallback (htmx falls back to window.confirm when we don't intercept).
function handleLinesBlockConfirm(evt) {
    var elt = evt.detail && evt.detail.elt;
    var btn = elt && elt.closest && elt.closest('[data-line-delete]');
    if (!btn) return;
    if (typeof window.showConfirmModal !== 'function') return;
    evt.preventDefault();
    var ds = btn.dataset;
    window.showConfirmModal({
        title: ds.confirmTitle || 'Delete this line?',
        message: ds.confirmMessage || '',
        confirmText: ds.confirmText || 'Delete',
        cancelText: ds.cancelText || 'Cancel',
        type: 'danger',
        onConfirm: function () {
            evt.detail.issueRequest(true);
        },
    });
}

// ========================================================
// htmx:afterSettle: re-run the CN modal init when the
// #invoice-detail subtree is itself swapped (which removes
// our old shell instance and replaces it with a fresh one).
// ========================================================

function handleInvoiceDetailSettle(evt) {
    var t = evt.detail && evt.detail.target;
    if (!t || t.id !== 'invoice-detail') return;
    // After ``hx-swap="outerHTML"`` the reference in ``evt.detail.target``
    // is the OLD, now-detached node — same caveat documented in
    // ``flowbite-reinit.js`` and handled in ``app.js`` afterSwap. If we
    // call ``reinitFlowbiteWidgets`` on the detached subtree, Flowbite's
    // ``Datepicker`` constructor still runs on the orphan ``<input>`` AND
    // re-registers itself in ``FlowbiteInstances`` under the same id with
    // ``override: true`` — silently replacing the live instance the
    // global afterSwap handler had just wired up. Concrete symptom: after
    // saving a payment, clicking the date input no longer opens the
    // picker. Resolve the live element by id so this re-run hits the DOM
    // the user actually sees.
    var live = document.getElementById('invoice-detail') || t;
    if (typeof window.reinitFlowbiteWidgets === 'function') {
        window.reinitFlowbiteWidgets(live);
    }
    cnModal = null;
    sendInvModal = null;
    initCnModal();
    initSendInvModal();
}

// ========================================================
// One-time global wiring
// ========================================================

function bindGlobalDelegation() {
    if (globalBound) return;
    globalBound = true;

    document.body.addEventListener('click', handleDelegatedClick);
    document.body.addEventListener('input', handleOverpaymentInput);
    document.body.addEventListener('input', handleAddLineTotalInput);

    // NOTE: We previously transformed paid_at dd/mm/yyyy → ISO here on
    // ``htmx:configRequest`` because the server only accepted ISO. The
    // ``PaymentForm`` now declares ``input_formats`` covering dd/mm/yyyy
    // directly, so the transform is redundant. It was also actively
    // harmful: on a failed POST the bound form re-rendered the input
    // with the ISO string, which Flowbite's dd/mm/yyyy datepicker then
    // mis-parsed (e.g. "19/04/2026" → "16/10/0024"). Form parses dd/mm/yyyy.
    document.body.addEventListener('htmx:beforeRequest', handleHtmxBeforeRequest);
    document.body.addEventListener('htmx:afterRequest', handleHtmxAfterRequest);
    document.body.addEventListener('htmx:afterSettle', handleInvoiceDetailSettle);

    // Draft line editing (#invoice-lines-block) — all no-op on pages /
    // invoices without the editable block.
    document.body.addEventListener('htmx:beforeRequest', handleLinesBlockBeforeRequest);
    document.body.addEventListener('htmx:afterSettle', handleLinesBlockSettle);
    document.body.addEventListener('htmx:responseError', handleLinesBlockResponseError);
    document.body.addEventListener('htmx:confirm', handleLinesBlockConfirm);
}

// ========================================================
// Entry points
// ========================================================

export function initInvoiceDetail(config) {
    config = config || {};
    var root = config.root || document.querySelector('[data-auto-init="invoiceDetail"]');
    if (!root) return;
    // CRITICAL: invalidate the module-level modal caches BEFORE
    // recreating them. ``createDynamicModal`` wires listeners onto the
    // specific ``#create-cn-modal`` / ``#send-inv-modal`` <div> it found
    // when first instantiated. After a boosted navigation (list → detail
    // → back → re-open another invoice) those <div>s are detached from
    // the DOM and replaced by fresh ones with the same id. Without this
    // reset the cached helper still targets the orphan node and
    // ``cnModal.open()`` / ``sendInvModal.open()`` silently no-op —
    // exactly the symptom users reported (Resend by Email + Create
    // Credit Note buttons "dead" until a hard refresh). Send Reminder
    // kept working because it's a plain ``hx-post`` form HTMX rewires
    // automatically via body-level event delegation.
    cnModal = null;
    sendInvModal = null;
    bindGlobalDelegation();
    initCnModal();
    initSendInvModal();
}

export function autoInitInvoiceDetail() {
    var root = document.querySelector('[data-auto-init="invoiceDetail"]');
    if (!root) return;
    initInvoiceDetail({ root: root });
}
